import React, { useState, useRef, useEffect } from 'react';

interface LazyVideoProps {
    src: string;
    webmSrc?: string;
    poster?: string;
    className?: string;
    onPlay?: () => void;
    controls?: boolean;
    playsInline?: boolean;
    videoId?: string;
    autoPlay?: boolean;
    loop?: boolean;
    muted?: boolean;
}

const LazyVideo: React.FC<LazyVideoProps> = ({
    src,
    webmSrc,
    poster,
    className = "w-full h-auto",
    onPlay,
    controls = true,
    playsInline = true,
    videoId,
    autoPlay = false,
    loop = false,
    muted = false
}) => {
    const [shouldLoad, setShouldLoad] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const [hasError, setHasError] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Si es autoplay, cargar inmediatamente
        if (autoPlay) {
            setShouldLoad(true);
            setIsInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        // Cargar video solo cuando está cerca del viewport
                        setTimeout(() => setShouldLoad(true), 200);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { rootMargin: '100px' } // Cargar 100px antes de ser visible
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, [autoPlay]);

    const handlePlay = () => {
        if (onPlay) onPlay();
    };

    const handleClick = () => {
        if (!shouldLoad) {
            setShouldLoad(true);
        }
    };

    const handleError = () => {
        setHasError(true);
    };

    // Si hay error, mostrar placeholder
    if (hasError) {
        return (
            <div 
                ref={containerRef}
                className={`${className} bg-gray-900 flex items-center justify-center`}
                style={{ aspectRatio: '16/9', minHeight: '200px' }}
            >
                <div className="text-white text-center">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm">Error al cargar el video</p>
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={className}>
            {shouldLoad && isInView ? (
                <video
                    ref={videoRef}
                    className={className}
                    controls={controls}
                    preload="none"
                    poster={poster}
                    onPlay={handlePlay}
                    onError={handleError}
                    playsInline={playsInline}
                    data-video-id={videoId}
                    autoPlay={autoPlay}
                    loop={loop}
                    muted={muted}
                >
                    {webmSrc && <source src={webmSrc} type="video/webm" />}
                    <source src={src} type="video/mp4" />
                    Tu navegador no soporta el elemento de video.
                </video>
            ) : (
                <div 
                    className={`${className} bg-gray-900 flex items-center justify-center cursor-pointer relative overflow-hidden`}
                    style={{ aspectRatio: '16/9', minHeight: '200px' }}
                    onClick={handleClick}
                >
                    {poster ? (
                        <>
                            <img 
                                src={poster} 
                                alt="Video thumbnail" 
                                className="w-full h-full object-cover opacity-70"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-20 transition-all">
                                <svg className="w-20 h-20 text-white opacity-90" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                </svg>
                            </div>
                        </>
                    ) : (
                        <div className="text-white text-center">
                            <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                            <p className="text-sm">Click para cargar video</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LazyVideo;

